import { useSelector } from "react-redux";
import { Driver } from "@app/domain";
import { Avatar, Button, Card } from "@app/design-system/components";
import { PhoneIcon, StarIcon } from "@app/design-system/icons";
import { CustomFieldsNavButton } from "@app/features/shared/CustomFieldsNavButton";
import { cx } from "@app/shared/utils/cx";
import { RootState } from "@app/store";
import { t } from "@app/utils/translation";
import styles from "./DriverCard.module.css";

export type DriverCardProps = {
  driver: Driver;
  /** Lets a host (e.g. Home's BottomPanel, which fuses this card flush into
   * a larger panel on mobile/tablet) override the default rounded/shadowed
   * look without forking the component — see BottomPanel.module.css. */
  className?: string;
  /** Order Details stacks this alongside its other sections as one grouped
   * list — see `Card`'s `grouped` prop. BottomPanel doesn't pass this; it
   * already fuses this card flush via its own `className` override. */
  grouped?: boolean;
  /** Order Details' Driver section extends this card with the same
   * Customer-section custom-fields navigation pattern — the badge + arrow
   * (see `CustomFieldsNavButton`) is the only clickable part of the card
   * besides the existing call button; the rest stays non-interactive.
   * Omit both to get the plain card Home's BottomPanel uses. */
  customFieldsCount?: number;
  onCustomFieldsClick?: () => void;
  /** Hides the call button entirely once the order is finished or canceled
   * by any party — mirrors legacy `components/Call-btn`'s exact behavior:
   * it doesn't render a disabled button, it renders nothing (see
   * `statusTone/isCallDisabled.ts`). Omit to always show it (matches legacy
   * behavior for callers that don't have a status handy). */
  callDisabled?: boolean;
};

export const DriverCard = ({
  driver,
  className,
  grouped,
  customFieldsCount,
  onCustomFieldsClick,
  callDisabled = false,
}: DriverCardProps) => {
  const language = useSelector((state: RootState) => state.setting.language);
  const hasCustomFields = !!customFieldsCount && customFieldsCount > 0;
  const hasRating = !!driver.rating;

  return (
    <Card grouped={grouped} className={cx(styles.card, className)}>
      <div className={styles.topRow}>
        <div className={styles.avatarWrap}>
          <Avatar name={driver.name} src={driver.avatarUrl} size="sm" className={styles.avatar} />
          {hasRating ? (
            <span className={styles.ratingBadge}>
              <StarIcon size={8} />
              {driver.rating.toFixed(1)}
            </span>
          ) : null}
        </div>
        <div className={styles.name}>{driver.name}</div>
        <div className={styles.vehicle}>
          <div className={styles.vehiclePlate}>{driver.vehicle.plate}</div>
          <div className={styles.vehicleModel}>
            {driver.vehicle.color} {driver.vehicle.model}
          </div>
        </div>
        {hasCustomFields ? <CustomFieldsNavButton count={customFieldsCount} onClick={onCustomFieldsClick!} /> : null}
      </div>
      {callDisabled ? null : (
        <Button
          variant="secondary"
          fullWidth
          leadingIcon={<PhoneIcon size={18} color="var(--color-call)" />}
          aria-label={`${t("Call", language)} ${driver.name}`}
          className={styles.callButton}
          onClick={() => {
            window.location.href = `tel:${driver.phone}`;
          }}
        >
            {t("CallDriver", language)}
        </Button>
      )}
    </Card>
  );
};
